import './line-item-list.scss'
import { LineItem } from '../LineItem/LineItem'
import type { Attribute, Cart } from '@/types/shopify'
import {
  isSubscriptionLineItem,
  getLineItemAttributeByKey,
} from '@/scripts/cart/utilities'
import { useState, type FC, useEffect, useMemo } from 'react'
import type { AdvancedCartItem } from '@/types/pages/cart'
import { CartPopOverSummary } from '../CartPopOverSummary/CartPopOverSummary'
import clsx from 'clsx'

interface LineItemListProps {
  cart: Cart
}

const addGiftItems = (
  cartItems: AdvancedCartItem[],
  giftItems: AdvancedCartItem[],
) => {
  giftItems.forEach((giftItem) => {
    const triggerId = giftItem.attributes.find(
      (attr) => attr.key === '_trigger_id',
    )?.value

    if (triggerId) {
      const parentItemIndex = cartItems.findIndex(
        (item) => item.merchandise.id === triggerId,
      )
      if (parentItemIndex !== -1) {
        cartItems.splice(parentItemIndex + 1, 0, giftItem)
      } else {
        cartItems.unshift(giftItem)
      }
    } else {
      cartItems.push(giftItem)
    }
  })

  return cartItems
}

export const LineItemList: FC<LineItemListProps> = ({ cart }) => {
  const [primaryBag, setPrimaryBag] = useState<AdvancedCartItem[]>([])
  const [secondaryBag, setSecondaryBag] = useState<AdvancedCartItem[]>([])

  const groupLinesToBags = (cart: Cart) => {
    const groups = cart.lines.reduce(
      (
        categories: any,
        proxyLine: any,
        index: string | number,
        lines: any[],
      ): any => {
        const line = JSON.parse(JSON.stringify(proxyLine)) as AdvancedCartItem
        const { quantity } = line

        const isSubscriptionItem = isSubscriptionLineItem(line)
        const deliveryZoneId = getLineItemAttributeByKey(
          line,
          '_delivery_zone_id',
        )
        if (!deliveryZoneId) {
          return categories
        }

        const rawAddons = getLineItemAttributeByKey(line, '_addons')
        if (rawAddons) {
          const addonsData = JSON.parse(rawAddons)
          for (const row of addonsData) {
            const addonIndex = lines.findIndex((line) =>
              line.merchandise.id.endsWith(row.id),
            )
            if (addonIndex === -1) {
              console.error('Addon not found')
              continue
            }
            line.addons = line.addons || []
            line.addons.push({
              ...lines[addonIndex],
              _addonQuantity: row.quantity,
            })
            categories.recognizedQuantity[addonIndex] -=
              quantity * Number(row.quantity)
          }
        }

        categories.recognizedQuantity[index] -= quantity

        const giftItem = line.attributes.find(
          (attr) => attr.key === '_gift' && attr.value === 'true',
        )

        if (giftItem) {
          categories.gifts.push(line)
          return categories
        }
        isSubscriptionItem
          ? categories.subscriptions.push(line)
          : categories.otps.push(line)
        return categories
      },
      {
        subscriptions: [] as AdvancedCartItem[],
        otps: [] as AdvancedCartItem[],
        gifts: [] as AdvancedCartItem[],
        recognizedQuantity: cart.lines.map(
          (line: { quantity: number }) => line.quantity,
        ),
      },
    ) as any

    groups.recognizedQuantity.forEach((quantity: number, index: number) => {
      if (quantity === 0) return
      const line = JSON.parse(JSON.stringify(cart.lines[index]))
      const isSubscriptionItem = isSubscriptionLineItem(line)
      const giftItem = line.attributes.find(
        (attr: Attribute) => attr.key === '_gift' && attr.value === 'true',
      )

      line.quantity = quantity
      if (giftItem) {
        groups.gifts.push(line)
        return
      }
      isSubscriptionItem
        ? groups.subscriptions.push(line)
        : groups.otps.push(line)
    })

    const hasSubscriptions = groups.subscriptions.length
    if (hasSubscriptions) {
      setPrimaryBag(groups.subscriptions)
      setSecondaryBag(addGiftItems(groups.otps, groups.gifts))
    } else {
      setPrimaryBag(addGiftItems(groups.otps, groups.gifts))
      setSecondaryBag([])
    }
  }

  const { summaryClasses, summaryWithBorderClasses } = useMemo(() => {
    const summaryClasses = ['cart-pop-over__line-item-list__summary']
    const summaryWithBorderClasses = ['cart-pop-over__summary']
    if (
      (primaryBag.length &&
        secondaryBag.length &&
        primaryBag.length + secondaryBag.length > 3) ||
      primaryBag.length > 3 ||
      secondaryBag.length > 3
    ) {
      summaryClasses.push('cart-pop-over__line-item-list__summary--overflow')
    } else {
      summaryWithBorderClasses.push('cart-pop-over__summary--with-border')
      summaryClasses.push('cart-pop-over__line-item-list__summary--normal')
    }
    return { summaryClasses, summaryWithBorderClasses }
  }, [primaryBag, secondaryBag])

  useEffect(() => {
    groupLinesToBags(cart)
  }, [cart])

  return (
    <div className="cart-pop-over__line-item-list">
      <div className="cart-pop-over__line-item-list__container">
        {!!primaryBag?.length && (
          <div className="cart-pop-over__line-item-list__bag">
            <h3 className="cart-pop-over__line-item-list__bag-label type-label">
              {secondaryBag.length > 0 ? 'subscriptions' : 'Your Bag'}
            </h3>
            <ul className="cart-pop-over__line-item-list__items-container">
              {primaryBag.map((primaryBagItem, index) => (
                <LineItem bagItem={primaryBagItem} key={index} />
              ))}
            </ul>
          </div>
        )}
        {!!secondaryBag?.length && (
          <div className="cart-pop-over__line-item-list__bag">
            <h3 className="cart-pop-over__line-item-list__bag-label type-label">
              one-time purchases
            </h3>
            <ul className="cart-pop-over__line-item-list__items-container">
              {secondaryBag.map((secondaryBagItem, index) => (
                <LineItem bagItem={secondaryBagItem} key={index} />
              ))}
            </ul>
          </div>
        )}
      </div>
      <div className={clsx(summaryClasses)}>
        <CartPopOverSummary cart={cart} classes={summaryWithBorderClasses} />
      </div>
    </div>
  )
}
